import { useState } from "react";
import { Ban, FileUp, Layers, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TARGET = 600;
const NEXT = { AVAILABLE: ["SUSPENDED", "VOIDED"], PAYMENT_PENDING: [], ACTIVATED: ["SUSPENDED", "VOIDED"], REGISTERED: ["SUSPENDED", "VOIDED"], SUSPENDED: ["RESTORE", "VOIDED"], VOIDED: [] };

function RegisterCard({ packages, onDone }) {
  const [f, setF] = useState({ serial: "", package_id: "", batch: "" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/cards", { ...f, package_id: f.package_id || packages[0]?.id, batch: f.batch || null });
      toast.success(`Registered ${f.serial.toUpperCase()}`);
      setF({ ...f, serial: "" });
      onDone();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="surface space-y-3 p-5" data-testid="register-card-form">
      <h2 className="flex items-center gap-2 text-sm font-bold"><Plus size={15} /> Register a serial</h2>
      <Field label="Card serial" hint="Enter exactly as printed on the card.">
        <input className="field font-mono uppercase" value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} placeholder="GIA-2026-000001" required data-testid="card-serial-input" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Package">
          <select className="field" value={f.package_id} onChange={(e) => setF({ ...f, package_id: e.target.value })} data-testid="card-package-select">
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Batch (optional)"><input className="field" value={f.batch} onChange={(e) => setF({ ...f, batch: e.target.value })} data-testid="card-batch-input" /></Field>
      </div>
      <ErrorBox error={error} />
      <button className="btn-primary w-full" data-testid="register-card-button">Register card</button>
    </form>
  );
}

const MAX_GEN = 600;
const serialAt = (p, n, d) => `${p.trim().toUpperCase()}${String(n).padStart(d, "0")}`;

function GenerateCards({ packages, onDone }) {
  const [f, setF] = useState({ prefix: `GIA-${new Date().getFullYear()}-`, start: 1, quantity: 100, digits: 6, package_id: "", batch: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const qty = Number(f.quantity) || 0;
  const valid = f.prefix.trim() && qty >= 1 && qty <= MAX_GEN && Number(f.start) >= 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Generate ${qty} serials from ${serialAt(f.prefix, Number(f.start), Number(f.digits))}?`)) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/cards/generate", { ...f, start: Number(f.start), quantity: qty, digits: Number(f.digits), package_id: f.package_id || packages[0]?.id, batch: f.batch || null });
      setResult(data); onDone(); toast.success(`${data.created} cards generated`);
      setF({ ...f, start: Number(f.start) + qty });
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="surface space-y-3 p-5" data-testid="generate-cards-form">
      <h2 className="flex items-center gap-2 text-sm font-bold"><Layers size={15} /> Generate serials in bulk</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Prefix"><input className="field font-mono uppercase" value={f.prefix} onChange={set("prefix")} required data-testid="generate-prefix-input" /></Field>
        <Field label="Digits"><input type="number" min={1} max={10} className="field" value={f.digits} onChange={set("digits")} data-testid="generate-digits-input" /></Field>
        <Field label="Start number"><input type="number" min={0} className="field" value={f.start} onChange={set("start")} required data-testid="generate-start-input" /></Field>
        <Field label={`Quantity (max ${MAX_GEN})`}><input type="number" min={1} max={MAX_GEN} className="field" value={f.quantity} onChange={set("quantity")} required data-testid="generate-quantity-input" /></Field>
        <Field label="Package">
          <select className="field" value={f.package_id} onChange={set("package_id")} data-testid="generate-package-select">
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Batch (optional)"><input className="field" value={f.batch} onChange={set("batch")} data-testid="generate-batch-input" /></Field>
      </div>
      {valid && <p className="rounded-xl bg-purple-50 p-3 font-mono text-[11px] text-purple-800" data-testid="generate-preview">{serialAt(f.prefix, Number(f.start), Number(f.digits))} → {serialAt(f.prefix, Number(f.start) + qty - 1, Number(f.digits))} · {qty} cards</p>}
      {qty > MAX_GEN && <p className="text-xs text-red-600" data-testid="generate-max-error">Maximum {MAX_GEN} serials per generation.</p>}
      <ErrorBox error={error} />
      <button className="btn-primary w-full" disabled={!valid || busy} data-testid="generate-cards-button">{busy && <Loader2 size={15} className="animate-spin" />} Generate {qty || ""} serials</button>
      {result && (
        <div className="rounded-xl bg-slate-50 p-3 text-xs" data-testid="generate-result">
          <p><b className="text-emerald-700">{result.created}</b> created · <b className="text-amber-700">{result.duplicates.length}</b> already existed (skipped)</p>
          <p className="mt-1 font-mono text-[10px] text-slate-500">{result.first} → {result.last}</p>
        </div>
      )}
    </form>
  );
}

function ImportCards({ packages, onDone }) {
  const [file, setFile] = useState(null);
  const [pkg, setPkg] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("file", file);
    fd.append("package_id", pkg || packages[0]?.id);
    setBusy(true);
    try { const { data } = await api.post("/admin/cards/import", fd); setResult(data); onDone(); toast.success(`${data.created} cards imported`); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="surface space-y-3 p-5" data-testid="import-cards-form">
      <h2 className="flex items-center gap-2 text-sm font-bold"><FileUp size={15} /> CSV import</h2>
      <p className="text-[11px] text-slate-500">One serial per row. Optional header <span className="font-mono">serial,batch</span>.</p>
      <input type="file" accept=".csv,text/csv" className="field" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="import-file-input" />
      <select className="field" value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="import-package-select">
        {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <button className="btn-secondary w-full" disabled={!file || busy} data-testid="import-cards-button">Import</button>
      {result && (
        <div className="rounded-xl bg-slate-50 p-3 text-xs" data-testid="import-result">
          <p><b className="text-emerald-700">{result.created}</b> created · <b className="text-amber-700">{result.duplicates.length}</b> duplicates · <b className="text-red-700">{result.invalid.length}</b> invalid</p>
          {result.duplicates.length > 0 && <p className="mt-1 break-all font-mono text-[10px] text-slate-500">Duplicates: {result.duplicates.slice(0, 20).join(", ")}</p>}
          {result.invalid.length > 0 && <p className="mt-1 break-all font-mono text-[10px] text-slate-500">Invalid: {result.invalid.slice(0, 20).join(", ")}</p>}
        </div>
      )}
    </form>
  );
}

function CardDetail({ serial, onClose, onChange }) {
  const { data: c, reload } = useFetch(`/admin/cards/${serial}`);
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const change = async () => {
    try { await api.patch(`/admin/cards/${serial}/status`, { status, reason }); toast.success("Card status updated"); setReason(""); reload(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="card-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">{serial}</DialogTitle></DialogHeader>
        {!c ? <Spinner /> : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2"><StatusBadge status={c.status} /> {c.customer && <span className="text-xs">Assigned to <b>{c.customer.full_name}</b></span>}</div>
            {c.purchaser && <p className="text-xs text-slate-600">Purchaser: <b>{c.purchaser.name}</b> · {c.purchaser.mobile} · {c.purchaser.id_type} ••{c.purchaser.id_number_last4}</p>}
            {NEXT[c.status]?.length > 0 && (
              <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                <p className="label">Change status</p>
                <div className="grid grid-cols-2 gap-2">
                  <select className="field" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="card-status-select">
                    <option value="">Select…</option>{NEXT[c.status].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input className="field" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="card-status-reason-input" />
                </div>
                <button className="btn-secondary btn-sm" disabled={!status || reason.length < 3} onClick={change} data-testid="card-status-apply"><Ban size={13} /> Apply</button>
              </div>
            )}
            <div>
              <p className="label">Activation & audit history</p>
              <div className="space-y-1.5">{c.history.map((h) => (
                <div key={h.id} className="rounded-lg border border-slate-100 px-3 py-2 text-xs"><b>{h.action}</b> · {h.actor_username} · {fmtDateTime(h.created_at)}</div>
              ))}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CardInventory() {
  const pkgs = useFetch("/admin/packages");
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [selected, setSelected] = useState(null);
  const qs = new URLSearchParams({ ...(filter.status && { status: filter.status }), ...(filter.q && { q: filter.q }), limit: "100" }).toString();
  const cards = useFetch(`/admin/cards?${qs}`);
  const total = cards.data ? Object.values(cards.data.counts).reduce((a, b) => a + b, 0) : 0;
  if (!pkgs.data) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Physical card inventory" title="Card inventory" sub="Manually assigned serials · unique · one-time redemption" />
      <div className="surface p-5" data-testid="inventory-progress">
        <div className="mb-2 flex justify-between text-sm"><span className="font-semibold">{total} registered</span><span className="text-slate-500">Target {TARGET}</span></div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full gradient-brand" style={{ width: `${Math.min(100, (total / TARGET) * 100)}%` }} /></div>
        <div className="mt-3 flex flex-wrap gap-2">
          {cards.data && Object.entries(cards.data.counts).map(([s, n]) => (
            <button key={s} onClick={() => setFilter({ ...filter, status: filter.status === s ? "" : s })} data-testid={`inventory-filter-${s}`}
              className={`rounded-full px-1 ${filter.status === s ? "ring-2 ring-purple-400" : ""}`}><StatusBadge status={s} /> <span className="font-mono text-xs">{n}</span></button>
          ))}
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[340px_1fr]">
        <div className="space-y-6">
          <GenerateCards packages={pkgs.data} onDone={cards.reload} />
          <RegisterCard packages={pkgs.data} onDone={cards.reload} />
          <ImportCards packages={pkgs.data} onDone={cards.reload} />
        </div>
        <div className="surface overflow-x-auto">
          <div className="relative p-4">
            <Search size={15} className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="field pl-9 font-mono uppercase" placeholder="Search serial" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="card-search-input" />
          </div>
          {cards.loading && !cards.data ? <Spinner /> : (
            <table className="data-table" data-testid="cards-table">
              <thead><tr><th>Serial</th><th>Package</th><th>Batch</th><th>Status</th><th>Registered</th></tr></thead>
              <tbody>{cards.data?.items.map((c) => (
                <tr key={c.serial} className="cursor-pointer" onClick={() => setSelected(c.serial)} data-testid={`card-row-${c.serial}`}>
                  <td className="font-mono font-semibold">{c.serial}</td>
                  <td className="text-xs">{pkgs.data.find((p) => p.id === c.package_id)?.name}</td>
                  <td className="text-xs">{c.batch || "—"}</td><td><StatusBadge status={c.status} /></td>
                  <td className="text-xs text-slate-500">{fmtDateTime(c.created_at)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {cards.data && <p className="p-4 text-xs text-slate-400">Showing {cards.data.items.length} of {cards.data.total}</p>}
        </div>
      </div>
      {selected && <CardDetail serial={selected} onClose={() => setSelected(null)} onChange={cards.reload} />}
    </div>
  );
}
